import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiFetch } from "@/lib/api";
import { formatarDataHora } from "@/lib/format";
import type { AuditEvento, PagedResult } from "@/lib/types";

export function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [evento, setEvento] = useState("");
  const [desde, setDesde] = useState("");
  const [ate, setAte] = useState("");

  const params = new URLSearchParams({ page: String(page), pageSize: "20" });
  if (evento.trim()) params.set("evento", evento.trim());
  if (desde) params.set("desde", new Date(desde).toISOString());
  if (ate) params.set("ate", new Date(ate).toISOString());

  const { data, isLoading } = useQuery({
    queryKey: ["audit", page, evento, desde, ate],
    queryFn: () => apiFetch<PagedResult<AuditEvento>>(`/api/admin/audit?${params}`),
  });

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-semibold">Audit log</h1>
        <p className="text-sm text-muted-foreground">
          Trilha de auditoria — login, acesso a fichas, criação e aceite de convites.
        </p>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Label htmlFor="evento" className="mb-1.5 block text-xs">
            Evento
          </Label>
          <Input
            id="evento"
            placeholder="ex: login"
            value={evento}
            onChange={(e) => {
              setEvento(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="w-48">
          <Label htmlFor="desde" className="mb-1.5 block text-xs">
            Desde
          </Label>
          <Input
            id="desde"
            type="datetime-local"
            value={desde}
            onChange={(e) => {
              setDesde(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="w-48">
          <Label htmlFor="ate" className="mb-1.5 block text-xs">
            Até
          </Label>
          <Input
            id="ate"
            type="datetime-local"
            value={ate}
            onChange={(e) => {
              setAte(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Eventos recentes</CardTitle>
          <CardDescription>{data ? `${data.total} evento(s)` : "Carregando..."}</CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quando</TableHead>
                <TableHead>Usuário</TableHead>
                <TableHead>Evento</TableHead>
                <TableHead>Recurso</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : data && data.items.length > 0 ? (
                data.items.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="text-muted-foreground">{formatarDataHora(a.criadoEm)}</TableCell>
                    <TableCell>{a.usuarioEmail ?? "—"}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{a.evento}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{a.recurso ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{a.ip ?? "—"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                    Nenhum evento encontrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <span className="text-sm text-muted-foreground">
            Página {data.page} de {data.totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={!data.hasPrevious} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft className="size-4" />
            Anterior
          </Button>
          <Button variant="outline" size="sm" disabled={!data.hasNext} onClick={() => setPage((p) => p + 1)}>
            Próxima
            <ChevronRight className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
